import {
  AccountMappedGrant,
  DeviceMappedGrant
} from 'src/clients/stratus/grants/types';
import IGrantsClient from '../../clients/stratus/grants/IGrantsClient';
import { SetServiceDependencies } from '../../infra/startup/types';
import IApplicationService from '../applicationService/IApplicationService';
import { IAuthProviderService } from '../authProviderService';
import { AuthContextEnum } from '../authTokenService';
import IAuthTokenService from '../authTokenService/IAuthTokenService';
import { GrantRepository } from './GrantRepository';
import { IGrantService } from './IGrantService';
import { PendingGrantRepository } from './PendingGrantRepository';
import {
  GrantContentResponseType,
  GrantOptionsType,
  GrantsInputType,
  GrantsResponseType,
  GrantType,
  MapGrantsParams
} from './types';
import { getServices } from '../../infra/startup';
import { PromiseHelper } from '../../utils/PromiseHelper';
import IEventService from '../eventService/IEventService';
import EventNames from '../../config/eventNames';
import { Refreshable } from '../../utils/refreshable/IRefreshable';
import { InternalService } from '../../core/services';

export class GrantService extends Refreshable implements IGrantService {
  private _authTokenService: IAuthTokenService;
  private _grantRepository: GrantRepository;
  private _pendingGrantRepository: PendingGrantRepository;
  private _grantClient: IGrantsClient;
  private _enabled: boolean;
  private _eventService: IEventService;
  private defaultKey = 'default';
  private initializedAuthContext: Map<
    AuthContextEnum | typeof this.defaultKey,
    boolean
  > = new Map();
  private promiseHelper = new PromiseHelper();

  constructor(props?: GrantsInputType) {
    super();
    this._enabled = props?.enabled || false;

    this.setRefreshMethods({
      init: () => {
        // TODO check if we need to call init here even it today we do not call.
        // this.init(props as any);
      },
      refresh: (props) => {
        this.refresh(props as any);
      }
    });
  }

  public setDependencies({
    services,
    repositories,
    clients
  }: SetServiceDependencies): void {
    const { authTokenService, eventService } = services;
    const { grantRepository } = repositories;
    const { stratus } = clients;

    this._authTokenService = authTokenService;
    this._grantRepository = grantRepository;
    this._grantClient = stratus.grantClient;
    this._pendingGrantRepository = repositories.pendingGrantRepository;
    this._eventService = eventService;
    this._markAsReady();
  }

  private shouldUpdateCache(
    srcGrants: GrantContentResponseType[],
    targetGrants: GrantContentResponseType[]
  ): boolean {
    return JSON.stringify(srcGrants) !== JSON.stringify(targetGrants);
  }

  private getGrantsFromCache(
    authContext: AuthContextEnum
  ): GrantContentResponseType[] {
    return this._grantRepository.findOne(
      this._authTokenService.getSuffix(authContext)
    );
  }

  private getPendingGrantsFromCache(
    authContext: AuthContextEnum
  ): GrantContentResponseType[] {
    return this._pendingGrantRepository.findOne(
      this._authTokenService.getSuffix(authContext)
    );
  }

  private saveGrantsToCache(
    grants: GrantContentResponseType[],
    authContext: AuthContextEnum
  ): void {
    this._grantRepository.save(
      this._authTokenService.getSuffix(authContext),
      grants
    );
  }

  private mapGrants({
    result,
    grantStatus
  }: MapGrantsParams): GrantsResponseType {
    const deviceItems = result?.devices?.items || [];

    const tenantId = getServices()?.tenantHandlerService?.getTenantId?.() || '';

    const deviceMapped: DeviceMappedGrant[] = deviceItems.flatMap((device) => {
      const deviceUuid = device?.deviceId || '';
      const list = (device?.grants?.[grantStatus] || []) as Array<{
        grant: string;
      }>;
      return list.map((g) => ({
        grant: g.grant,
        level: 'DEVICE' as const,
        deviceCloudId: deviceUuid,
        tenantId
      }));
    });

    const accountMapped: AccountMappedGrant[] = (
      (result?.account?.grants?.[grantStatus] || []) as Array<{ grant: string }>
    ).map((g) => ({
      grant: g.grant,
      level: 'ACCOUNT' as const,
      tenantId
    }));
    return {
      contents: [...deviceMapped, ...accountMapped]
    };
  }

  private savePendingGrantsToCache(
    grants: GrantContentResponseType[],
    authContext: AuthContextEnum
  ): void {
    this._pendingGrantRepository.save(
      this._authTokenService.getSuffix(authContext),
      grants
    );
  }

  private async getAllGrantsFromClient(): Promise<{
    grants: GrantContentResponseType[];
    pendingGrants: GrantContentResponseType[];
  }> {
    const result = await this._grantClient.getGrants();
    const grants =
      this.mapGrants({
        result,
        grantStatus: 'active'
      }).contents || [];

    const pendingGrants =
      this.mapGrants({
        result,
        grantStatus: 'pending'
      }).contents || [];

    return { grants, pendingGrants };
  }

  private checkGrantMatch(
    grantToCheck: GrantType,
    cachedGrant: GrantContentResponseType
  ): boolean {
    const tests = [
      grantToCheck.grant === cachedGrant.grant,
      !grantToCheck?.level || grantToCheck.level === cachedGrant.level,
      !grantToCheck?.deviceId ||
        grantToCheck.deviceId === cachedGrant.deviceCloudId,
      !grantToCheck?.tenantId || grantToCheck.tenantId === cachedGrant.tenantId
    ];

    return tests.every(Boolean);
  }

  public async checkGrants(
    grants: GrantType[],
    options?: GrantOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;
    const promiseKey = options?.authContext ?? this.defaultKey;
    const promise = this.promiseHelper.getPendingPromise(promiseKey);

    if (promise !== undefined) {
      await promise;
    }

    const authContext =
      options?.authContext || this._authTokenService.getCurrentContext();

    const contents = this.getGrantsFromCache(authContext);

    return grants?.every?.((grantToCheck) =>
      contents?.some?.((cachedGrant: GrantContentResponseType) =>
        this.checkGrantMatch(grantToCheck, cachedGrant)
      )
    );
  }

  public async checkPendingGrants(
    pendingGrants: GrantType[],
    options?: GrantOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    const authContext =
      options?.authContext || this._authTokenService.getCurrentContext();

    const contents = this.getPendingGrantsFromCache(authContext);

    return pendingGrants?.every?.((pendingGrantToCheck) =>
      contents?.some?.((cachedPendingGrant: GrantContentResponseType) =>
        this.checkGrantMatch(pendingGrantToCheck, cachedPendingGrant)
      )
    );
  }

  public async haveGrantsChanged(options?: GrantOptionsType): Promise<{
    haveGrantsChanged: boolean;
    havePendingGrantsChanged: boolean;
  }> {
    if (!this.isEnabled())
      return { haveGrantsChanged: false, havePendingGrantsChanged: false };

    const authContext =
      options?.authContext || this._authTokenService.getCurrentContext();

    const { grants, pendingGrants } = await this.getAllGrantsFromClient();
    const cachedGrants = this.getGrantsFromCache(authContext);
    const cachedPendingGrants = this.getPendingGrantsFromCache(authContext);

    let haveGrantsChanged = false;
    let havePendingGrantsChanged = false;

    if (this.shouldUpdateCache(grants, cachedGrants)) {
      this.saveGrantsToCache(grants, authContext);
      haveGrantsChanged = true;
    }

    if (this.shouldUpdateCache(pendingGrants, cachedPendingGrants)) {
      this.savePendingGrantsToCache(pendingGrants, authContext);
      havePendingGrantsChanged = true;
    }

    return { haveGrantsChanged, havePendingGrantsChanged };
  }

  public async getGrant(
    grant: GrantType,
    options?: GrantOptionsType
  ): Promise<GrantType> {
    if (!this.isEnabled()) return undefined;

    const authContext =
      options?.authContext || this._authTokenService.getCurrentContext();

    const grantsFromCache = this.getGrantsFromCache(authContext);

    return grantsFromCache.find(
      (grantFromCache) =>
        grantFromCache.grant === grant.grant &&
        grantFromCache.level === grant.level
    );
  }

  public async getPendingGrant(
    pendingGrant: GrantType,
    options?: GrantOptionsType
  ): Promise<GrantType> {
    if (!this.isEnabled()) return undefined;

    const authContext =
      options?.authContext || this._authTokenService.getCurrentContext();

    const pendingGrantsFromCache = this.getPendingGrantsFromCache(authContext);

    return pendingGrantsFromCache.find(
      (pendingGrantFromCache) =>
        pendingGrantFromCache.grant === pendingGrant.grant &&
        pendingGrantFromCache.level === pendingGrant.level
    );
  }

  public async init(options?: GrantOptionsType): Promise<void> {
    const contextKey = options?.authContext ?? this.defaultKey;

    let promise = this.promiseHelper.getPendingPromise(contextKey);
    const isInitialized = this.initializedAuthContext.get(contextKey);

    if (!isInitialized) {
      this.initializedAuthContext.set(contextKey, true);
      if (promise === undefined) {
        promise = this.refresh(options);
        this.promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    await promise;
  }

  private async refreshAction(options?: GrantOptionsType): Promise<void> {
    const { haveGrantsChanged, havePendingGrantsChanged } =
      await this.haveGrantsChanged(options);
    if (haveGrantsChanged) {
      void this._eventService.publish(
        EventNames.shellGrantsChangedEventName,
        undefined
      );
    }
    if (havePendingGrantsChanged) {
      void this._eventService.publish(
        EventNames.shellPendingGrantsChangedEventName,
        undefined
      );
    }
  }

  public async refresh(options?: GrantOptionsType): Promise<void> {
    const { graphQLService } = getServices();
    await (graphQLService as unknown as InternalService).isReady;
    const promiseKey = options?.authContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(promiseKey);
    if (this.isEnabled() && promise === undefined) {
      promise = this.refreshAction(options);
      this.promiseHelper.setPendingPromise(promiseKey, promise);
    }
    await promise;
  }

  private useReactCheckBase<T>(
    _React: any,
    items: T,
    options: GrantOptionsType | undefined,
    checkMethod: (items: T, options?: GrantOptionsType) => Promise<boolean>,
    hookName: string,
    dependencyKey: string
  ): {
    data?: boolean;
    error?: Error;
    loading: boolean;
    reload: () => Promise<void>;
  } {
    const [result, setResult] = _React.useState({
      data: undefined,
      error: undefined,
      loading: true
    });

    const isMountedRef = _React.useRef(true);
    const dependenciesRef = _React.useRef({
      [dependencyKey]: items,
      options,
      handleDataCount: 0
    });
    dependenciesRef.current[dependencyKey] = items;
    dependenciesRef.current.options = options;

    const handleData = _React.useCallback(async () => {
      const thisCount = dependenciesRef.current.handleDataCount + 1;
      dependenciesRef.current.handleDataCount = thisCount;

      await this.init(dependenciesRef.current.options);

      const newData = await checkMethod(
        dependenciesRef.current[dependencyKey],
        dependenciesRef.current.options
      )
        .then((data) => ({ data, error: undefined, loading: false }))
        .catch((error) => ({ data: undefined, error, loading: false }));

      if (!isMountedRef.current) {
        console.warn(`${hookName}: component unmounted before receiving data`);
      } else if (thisCount !== dependenciesRef.current.handleDataCount) {
        console.warn(
          `${hookName}: race condition detected, discarding stale data`
        );
      } else {
        setResult(newData);
      }
    }, []);

    _React.useEffect(() => {
      handleData();
    }, [
      handleData,
      JSON.stringify(items),
      options?.authContext,
      options?.offset
    ]);

    _React.useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    return _React.useMemo(
      () => ({
        ...result,
        reload: () => handleData()
      }),
      [result, handleData]
    );
  }

  public useReactCheckGrants(
    _React: any,
    grants: GrantType[],
    options?: GrantOptionsType
  ): {
    data?: boolean;
    error?: Error;
    loading: boolean;
    reload: () => Promise<void>;
  } {
    return this.useReactCheckBase(
      _React,
      grants,
      options,
      this.checkGrants.bind(this),
      'Grants.useReactCheckGrants',
      'grants'
    );
  }

  public useReactCheckPendingGrants(
    _React: any,
    pendingGrants: GrantType[],
    options?: GrantOptionsType
  ): {
    data?: boolean;
    error?: Error;
    loading: boolean;
    reload: () => Promise<void>;
  } {
    return this.useReactCheckBase(
      _React,
      pendingGrants,
      options,
      this.checkPendingGrants.bind(this),
      'Grants.useReactCheckPendingGrants',
      'pendingGrants'
    );
  }

  public isEnabled(): boolean {
    if (!this._enabled) {
      console.warn('Grants: disabled');
      return false;
    }
    return true;
  }
}
